import {animated} from 'react-spring';

import styled, {css} from 'styled-components';

import BaseCloseIcon from '@edna/icons/close.svg';

import {mixins} from '../styles';
import {DEFAULT_NOTIFICATION_OFFSET, ENotificationType, TNewNotification} from './types';
import {isDarkNotification} from './utils';

export const Notifications = styled.div<{topOffset?: number}>`
  width: 100%;
  ${({theme: {zIndex, animation}, topOffset = DEFAULT_NOTIFICATION_OFFSET}) => css`
    ${mixins.position({position: 'fixed', top: `${topOffset}px`})};
    z-index: ${zIndex.notifications};
    transition: top ${animation.duration}ms ease-in-out;
  `}
`;

export const PermanentNotifications = styled(Notifications)`
  position: static;
`;

export const ItemWrapper = styled(animated.div)`
  padding-bottom: ${({theme: {spacing}}) => spacing.s4};
`;

type TItemProps = {
  type?: TNewNotification['type'];
};

const getItemBackgroundColor = ({type, theme}: TTheme & TItemProps) => {
  switch (type) {
    case ENotificationType.success:
    case ENotificationType.SUCCESS:
      return theme.palette.green400;
    case ENotificationType.warning:
    case ENotificationType.WARNING:
      return theme.palette.yellow500;
    case ENotificationType.error:
    case ENotificationType.ERROR:
      return theme.palette.red400;
    case ENotificationType.info:
    case ENotificationType.INFO:
    default:
      return theme.palette.grey500;
  }
};

const getItemTextColor = ({type, theme}: TTheme & TItemProps) =>
  isDarkNotification(type) ? theme.branding.content.primary : theme.palette.white;

type TNotificationButtonProps = TItemProps & {
  hasMarginLeft?: boolean;
};

export const NotificationButton = styled.button<TNotificationButtonProps>`
  ${mixins.pureButton};
  ${mixins.f2};
  position: relative;
  line-height: 0;
  vertical-align: middle;
  font-weight: 500;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  padding: 7px 16px;
  background-color: transparent;
  color: ${getItemTextColor};
  border-color: ${getItemTextColor};
  text-decoration: none;

  svg {
    ${mixins.wh('16px')};
    flex-shrink: 0;
  }

  &:hover {
    color: ${getItemBackgroundColor};
    background-color: ${getItemTextColor};
  }

  ${({theme, type, hasMarginLeft}) => css`
    gap: ${theme.spacing.s1};
    height: ${theme.dimensions.controlHeightS}px;
    margin-left: ${hasMarginLeft ? theme.spacing.s3 : 0};
    border: 2px solid ${getItemTextColor({theme, type})};
    border-radius: 6px;
    transition: all ${theme.animation.duration}ms;
  `}
`;

export const Content = styled.div`
  ${mixins.flexAlign('flex-start', 'start')}
  column-gap: ${({theme}) => theme.spacing.s3};
  align-self: center;
`;

export const Icon = styled.svg`
  ${mixins.wh('24px')};
  position: relative;
  flex-shrink: 0;
`;

export const Item = styled.div<TItemProps>`
  ${mixins.flexAlign('space-between', 'start')};
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 56px;
  background-color: ${getItemBackgroundColor};
  color: ${getItemTextColor};

  ${({theme}) => css`
    column-gap: ${theme.spacing.s3};
    padding: 12px 18px 12px 16px;
    border-radius: ${theme.borderRadius.br4};
    box-shadow: ${theme.boxShadow.notification};
  `}

  &:has(${String(NotificationButton)}) {
    ${String(Icon)} {
      top: 2px;
    }

    ${String(NotificationButton)} {
      color: ${getItemTextColor};
      border-color: ${getItemTextColor};

      /* stylelint-disable-next-line max-nesting-depth */
      &:hover {
        color: ${getItemBackgroundColor};
        background-color: ${getItemTextColor};
      }
    }
  }
`;

export const CloseIcon = styled(BaseCloseIcon)<{size: 'M' | 'L'}>`
  cursor: pointer;
  ${({size}) => mixins.wh(size === 'M' ? '20px' : '24px')};
  ${mixins.flexAlign('center')};
`;

export const Title = styled.div`
  ${mixins.f4};
`;

export const Message = styled.div`
  align-self: center;
`;
